// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { useMemo, useState } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { MlModelsPanel } from '@/features/course-trace/MlModelsPanel';
import { MlThresholdProfilesPanel } from '@/features/course-trace/MlThresholdProfilesPanel';
import {
  useMlJobs,
  useMlTrainingData,
  type MlJob,
  type MlTrainingCourse,
  type MlThresholds,
} from '@/features/ml/useMlTraining';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** The three tools `training/` ships, in the order they are run. */
const TOOLS = ['extract', 'train', 'evaluate', 'tune'] as const;
type Tool = (typeof TOOLS)[number];

/** Providers the pipeline knows. The list is a fallback — what is stored wins (see `providers`). */
const KNOWN_PROVIDERS = ['lantmateriet-orto', 'esri-worldimagery'];

/** The kinds a label count is shown for, in the order the detector cares about them. */
const LABEL_KINDS = ['bunker', 'fairway', 'tee', 'green', 'water', 'trees'];

/**
 * Everything about the detection models in one developer screen: what has been trained, what
 * there is left to train on, and the button that queues a run.
 *
 * It exists because the answer to "can we train on Lantmateriet imagery yet" used to require
 * reading `training/data/manifest.json` on somebody's laptop and running the extractor's own
 * SQL by hand. Data — not code — has been the bottleneck the whole time, so the training-set
 * half of this page is the important half: it names every course that clears the richness
 * floor, every course that misses it and by how much, and links straight to the OpenStreetMap
 * element and the stored imagery each one would be cut from.
 *
 * Developer-or-admin gated, the same bar as the batch fleet (see BFF
 * GeodataEndpoints.ResolveDeveloperCaller): pipeline detail, not course data.
 */
export default function MlTrainingScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { state: data, refresh: refreshData } = useMlTrainingData(apiBaseUrl, user?.token, canView);
  const { state: jobs, refresh: refreshJobs, queue } = useMlJobs(apiBaseUrl, user?.token, canView);

  const providers = useMemo(() => {
    if (data.status !== 'ready') return KNOWN_PROVIDERS;
    const stored = new Set<string>();
    for (const course of data.courses) {
      for (const image of course.images) stored.add(image.provider);
    }
    // Whatever is actually stored, then anything known that is not — so a provider with no
    // imagery yet can still be picked for an extract run that will go and fetch it.
    return [...stored, ...KNOWN_PROVIDERS.filter((p) => !stored.has(p))];
  }, [data]);

  return (
    <>
      <Stack.Screen options={{ title: t('mlTrainingPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('mlTrainingPage.title')}</Text>
        <Text style={styles.intro}>{t('mlTrainingPage.intro')}</Text>

        {!canView ? (
          <Text style={styles.paragraph} testID="ml-training-forbidden">
            {t('mlTrainingPage.forbidden')}
          </Text>
        ) : (
          <>
            <Text style={styles.heading}>{t('mlTrainingPage.runHeading')}</Text>
            <RunPanel
              providers={providers}
              worker={jobs.status !== 'ready' ? 'unknown' : jobs.workerOnline ? 'online' : 'offline'}
              onQueue={queue}
              t={t}
            />

            <Text style={styles.heading}>{t('mlTrainingPage.jobsHeading')}</Text>
            {jobs.status === 'loading' ? (
              <Text style={styles.paragraph}>{t('mlTrainingPage.loading')}</Text>
            ) : jobs.status === 'forbidden' ? (
              <Text style={styles.paragraph} testID="ml-training-forbidden">
                {t('mlTrainingPage.forbidden')}
              </Text>
            ) : jobs.status === 'failed' ? (
              <>
                <Text style={styles.paragraph} testID="ml-training-jobs-failed">
                  {t('mlTrainingPage.jobsFailed')}
                  {jobs.httpStatus ? ` ${t('mlTrainingPage.httpStatus', { status: jobs.httpStatus })}` : ''}
                </Text>
                <SmallButton onPress={refreshJobs} label={t('mlTrainingPage.retry')} testID="ml-training-jobs-retry" />
              </>
            ) : jobs.jobs.length === 0 ? (
              <Text style={styles.paragraph} testID="ml-training-no-jobs">
                {t('mlTrainingPage.noJobs')}
              </Text>
            ) : (
              jobs.jobs.map((job) => <JobCard key={job.jobId} job={job} t={t} />)
            )}

            <Text style={styles.heading}>{t('mlTrainingPage.modelsHeading')}</Text>
            <MlModelsPanel apiBaseUrl={apiBaseUrl} token={user?.token} initiallyOpen />

            <Text style={styles.heading}>{t('mlTrainingPage.thresholdProfilesHeading')}</Text>
            <MlThresholdProfilesPanel apiBaseUrl={apiBaseUrl} token={user?.token} />

            <View style={styles.refreshRow}>
              <Text style={styles.heading}>{t('mlTrainingPage.dataHeading')}</Text>
              <SmallButton onPress={refreshData} label={t('mlTrainingPage.refresh')} testID="ml-training-data-refresh" />
            </View>

            {data.status === 'loading' ? (
              <Text style={styles.paragraph}>{t('mlTrainingPage.loading')}</Text>
            ) : data.status === 'forbidden' ? (
              <Text style={styles.paragraph} testID="ml-training-forbidden">
                {t('mlTrainingPage.forbidden')}
              </Text>
            ) : data.status === 'failed' ? (
              <>
                <Text style={styles.paragraph} testID="ml-training-data-failed">
                  {t('mlTrainingPage.dataFailed')}
                  {data.httpStatus ? ` ${t('mlTrainingPage.httpStatus', { status: data.httpStatus })}` : ''}
                </Text>
                <SmallButton onPress={refreshData} label={t('mlTrainingPage.retry')} testID="ml-training-data-retry" />
              </>
            ) : data.courses.length === 0 ? (
              <Text style={styles.paragraph} testID="ml-training-no-data">
                {t('mlTrainingPage.noData')}
              </Text>
            ) : (
              <>
                <ThresholdSummary
                  thresholds={data.thresholds}
                  eligible={data.courses.filter((c) => c.eligible).length}
                  total={data.courses.length}
                  t={t}
                />
                {data.courses.map((course) => (
                  <CourseCard key={course.courseId} course={course} t={t} />
                ))}
              </>
            )}
          </>
        )}
      </ScrollView>
    </>
  );
}

/**
 * The form that queues a run.
 *
 * The argv it will send is shown verbatim before it is sent, because these tools are run from a
 * shell far more often than from here — seeing the same command means the page never becomes a
 * second, differently-behaving way to do the same thing.
 */
function RunPanel({
  providers,
  worker,
  onQueue,
  t,
}: {
  providers: string[];
  /**
   * Three states, not two. Until the job queue has been read this is `unknown` — saying "no ML
   * worker is online" from a request that failed is a claim about the fleet made out of no
   * information, and it contradicted the batch-fleet page, which was reading the same rows.
   */
  worker: 'online' | 'offline' | 'unknown';
  onQueue: (
    kind: string,
    args: string[]
  ) => Promise<{ ok: boolean; message?: string | null; jobId?: string | null; httpStatus?: number | null }>;
  t: Translate;
}) {
  const [tool, setTool] = useState<Tool>('train');
  const [provider, setProvider] = useState(providers[0] ?? KNOWN_PROVIDERS[0]);
  const [nir, setNir] = useState(false);
  const [register, setRegister] = useState(true);
  const [fromS3, setFromS3] = useState(true);
  const [epochs, setEpochs] = useState('20');
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const args = useMemo(
    () => buildArgs({ tool, provider, nir, register, fromS3, epochs, extra }),
    [tool, provider, nir, register, fromS3, epochs, extra]
  );

  const run = async () => {
    setBusy(true);
    setResult(null);
    const outcome = await onQueue(tool, args);
    setBusy(false);
    setResult(
      outcome.ok
        ? { ok: true, message: t('mlTrainingPage.queued', { tool }) }
        : {
            ok: false,
            message: [
              outcome.message || t('mlTrainingPage.queueFailed'),
              outcome.httpStatus ? t('mlTrainingPage.httpStatus', { status: outcome.httpStatus }) : null,
            ]
              .filter(Boolean)
              .join(' '),
          }
    );
  };

  return (
    <View style={styles.card} testID="ml-training-run-panel">
      {worker === 'unknown' ? (
        <Text style={styles.hint} testID="ml-training-worker-unknown">
          {t('mlTrainingPage.workerUnknown')}
        </Text>
      ) : worker === 'offline' ? (
        // Not an error and not a blocker: the job simply sits in the queue. Said plainly because
        // the ML worker runs off-cloud on a developer's own machine and is usually not running.
        <Text style={styles.warning} testID="ml-training-worker-offline">
          {t('mlTrainingPage.workerOffline')}
        </Text>
      ) : (
        <Text style={styles.ok} testID="ml-training-worker-online">
          {t('mlTrainingPage.workerOnline')}
        </Text>
      )}

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.tool')}</Text>
      <View style={styles.chipRow}>
        {TOOLS.map((value) => (
          <Chip
            key={value}
            label={t(`mlTrainingPage.tools.${value}`)}
            selected={tool === value}
            testID={`ml-training-tool-${value}`}
            onPress={() => setTool(value)}
          />
        ))}
      </View>
      <Text style={styles.hint}>{t(`mlTrainingPage.toolHints.${tool}`)}</Text>

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.provider')}</Text>
      <View style={styles.chipRow}>
        {providers.map((value) => (
          <Chip
            key={value}
            label={value}
            selected={provider === value}
            testID={`ml-training-provider-${value}`}
            onPress={() => setProvider(value)}
          />
        ))}
      </View>

      <View style={styles.chipRow}>
        {tool === 'extract' ? (
          <Chip
            label={t('mlTrainingPage.fromS3')}
            selected={fromS3}
            testID="ml-training-from-s3"
            onPress={() => setFromS3((v) => !v)}
          />
        ) : null}
        {tool === 'train' ? (
          <Chip
            label={t('mlTrainingPage.nir')}
            selected={nir}
            testID="ml-training-nir"
            onPress={() => setNir((v) => !v)}
          />
        ) : null}
        <Chip
          label={t('mlTrainingPage.register')}
          selected={register}
          testID="ml-training-register"
          onPress={() => setRegister((v) => !v)}
        />
      </View>

      {tool === 'train' ? (
        <>
          <Text style={styles.fieldLabel}>{t('mlTrainingPage.epochs')}</Text>
          <TextInput
            style={styles.input}
            value={epochs}
            onChangeText={setEpochs}
            keyboardType="numeric"
            testID="ml-training-epochs"
          />
        </>
      ) : null}

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.extraArgs')}</Text>
      <TextInput
        style={styles.input}
        value={extra}
        onChangeText={setExtra}
        autoCapitalize="none"
        autoCorrect={false}
        placeholder={t('mlTrainingPage.extraArgsPlaceholder')}
        placeholderTextColor={appColors.text.muted}
        testID="ml-training-extra-args"
      />

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.command')}</Text>
      <Text style={styles.command} testID="ml-training-command">
        {commandLine(tool, args)}
      </Text>

      <Pressable
        style={[styles.runButton, busy && styles.runButtonDisabled]}
        disabled={busy}
        testID="ml-training-run"
        accessibilityRole="button"
        onPress={run}
      >
        <Text style={styles.runButtonText}>
          {busy ? t('mlTrainingPage.queueing') : t('mlTrainingPage.queue')}
        </Text>
      </Pressable>

      {result ? (
        <Text
          style={result.ok ? styles.ok : styles.errorText}
          testID={result.ok ? 'ml-training-queued' : 'ml-training-queue-failed'}
        >
          {result.message}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * The argv for a run.
 *
 * `--dsn` is deliberately absent: geodata rejects it outright, because the caller does not know
 * the worker's tunnel and the worker supplies its own.
 */
export function buildArgs({
  tool,
  provider,
  nir,
  register,
  fromS3,
  epochs,
  extra,
}: {
  tool: Tool;
  provider: string;
  nir: boolean;
  register: boolean;
  fromS3: boolean;
  epochs: string;
  extra: string;
}): string[] {
  const args: string[] = ['--provider', provider];

  if (tool === 'extract') {
    args.push('--out', 'training/data');
    // Stored basemaps rather than a live tile fetch: a course already analysed with this
    // provider has its mosaic in S3, and re-fetching it costs the provider's quota for pixels
    // that have not changed.
    if (fromS3) args.push('--imagery-source', 's3');
  }

  if (tool === 'train') {
    const parsed = Number.parseInt(epochs, 10);
    if (Number.isFinite(parsed) && parsed > 0) args.push('--epochs', String(parsed));
    if (nir) args.push('--nir');
  }

  if (register) args.push('--register');

  // Split on whitespace, the way a shell would. Quoting is not supported on purpose: an
  // argument needing quotes is a sign this should be run from a terminal instead.
  const words = extra.trim().split(/\s+/).filter((word) => word.length > 0);
  args.push(...words);

  return args;
}

/** The command as it will run on the worker, for the developer to recognise or copy. */
export function commandLine(tool: Tool, args: string[]): string {
  const script =
    tool === 'extract' ? 'extract_dataset.py' : tool === 'tune' ? 'tune_thresholds.py' : `${tool}.py`;
  return `python3 training/${script} ${args.join(' ')}`;
}

function ThresholdSummary({
  thresholds,
  eligible,
  total,
  t,
}: {
  thresholds: MlThresholds;
  eligible: number;
  total: number;
  t: Translate;
}) {
  return (
    <View style={styles.card} testID="ml-training-threshold-summary">
      <Text style={styles.cardTitle}>
        {t('mlTrainingPage.eligibleCount', { eligible, total })}
      </Text>
      <Text style={styles.meta}>
        {t('mlTrainingPage.thresholds', {
          bunkers: thresholds.minBunkers,
          fairways: thresholds.minFairways,
        })}
      </Text>
    </View>
  );
}

function CourseCard({ course, t }: { course: MlTrainingCourse; t: Translate }) {
  return (
    <View style={styles.card} testID={`ml-training-course-${course.courseId}`}>
      <View style={styles.cardHeaderRow}>
        <Text style={styles.cardTitle}>{course.name || t('mlTrainingPage.unnamedCourse')}</Text>
        <Text style={course.eligible ? styles.badgeEligible : styles.badgeShort}>
          {course.eligible ? t('mlTrainingPage.eligible') : t('mlTrainingPage.notEligible')}
        </Text>
      </View>

      <Text style={styles.meta}>
        {LABEL_KINDS.map((kind) => `${kind} ${course.labelCounts[kind] ?? 0}`).join('  ·  ')}
      </Text>

      {course.shortfall.length > 0 ? (
        <Text style={styles.warning} testID={`ml-training-shortfall-${course.courseId}`}>
          {course.shortfall
            .map((s) =>
              t('mlTrainingPage.shortfall', {
                kind: s.kind,
                missing: s.required - s.has,
                required: s.required,
              })
            )
            .join('  ·  ')}
        </Text>
      ) : null}

      <View style={styles.tagRow}>
        {course.osmUrl ? (
          <LinkTag label={t('mlTrainingPage.openInOsm')} url={course.osmUrl} testID={`ml-training-osm-${course.courseId}`} />
        ) : (
          <Text style={styles.mutedTag} testID={`ml-training-no-osm-${course.courseId}`}>
            {t('mlTrainingPage.noOsm')}
          </Text>
        )}
        {course.images.map((image) => (
          <LinkTag
            key={`${image.provider}-${image.kind}`}
            label={`${image.provider} · ${image.kind}`}
            url={image.url}
            testID={`ml-training-image-${course.courseId}-${image.provider}-${image.kind}`}
          />
        ))}
        {course.images.length === 0 ? (
          <Text style={styles.mutedTag}>{t('mlTrainingPage.noImages')}</Text>
        ) : null}
      </View>
    </View>
  );
}

function JobCard({ job, t }: { job: MlJob; t: Translate }) {
  const failed = job.exitCode !== null && job.exitCode !== 0;
  // A tuning run prints a few hundred lines of coordinate descent, and the widest of them —
  // the crop-id list, the PROVIDER_THRESHOLDS block — are far wider than the card. Rendered
  // inline they pushed the run history into a wall of monospace with its right-hand end cut
  // off, so the one thing worth reading (the overfit warning, the exit code) was buried in it.
  // Behind a button the history is a list of runs again, and the log gets a window that can
  // scroll both ways.
  const [outputOpen, setOutputOpen] = useState(false);
  const toolName = t(`mlTrainingPage.tools.${job.kind}`, {});

  return (
    <View style={styles.card} testID={`ml-training-job-${job.jobId}`}>
      <View style={styles.cardHeaderRow}>
        <Text style={styles.cardTitle}>{toolName}</Text>
        <Text style={failed ? styles.badgeShort : styles.badgeStatus}>{job.status}</Text>
        {job.workerId ? <Text style={styles.meta}>{job.workerId}</Text> : null}
      </View>
      <Text style={styles.command}>{job.args.join(' ') || '—'}</Text>
      <Text style={styles.meta}>
        {job.requestedAt}
        {job.durationSeconds !== null ? `  ·  ${job.durationSeconds}s` : ''}
        {job.exitCode !== null ? `  ·  exit ${job.exitCode}` : ''}
      </Text>
      {job.error ? <Text style={styles.errorText}>{job.error}</Text> : null}
      {job.outputTail ? (
        <Pressable
          testID={`ml-training-output-open-${job.jobId}`}
          accessibilityRole="button"
          style={styles.outputButton}
          onPress={() => setOutputOpen(true)}
        >
          <Text style={styles.outputButtonText}>{t('mlTrainingPage.showOutput')}</Text>
        </Pressable>
      ) : null}

      <Modal
        visible={outputOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setOutputOpen(false)}
      >
        <Pressable style={styles.outputOverlay} onPress={() => setOutputOpen(false)}>
          {/* Swallows the tap so clicking the log itself does not dismiss the window. */}
          <Pressable style={styles.outputCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.outputHeader}>
              <Text style={styles.outputTitle}>
                {t('mlTrainingPage.outputTitle', { tool: toolName })}
              </Text>
              <Pressable
                testID={`ml-training-output-close-${job.jobId}`}
                accessibilityRole="button"
                accessibilityLabel={t('menu.close')}
                hitSlop={8}
                onPress={() => setOutputOpen(false)}
              >
                <Text style={styles.outputClose}>{t('menu.close')}</Text>
              </Pressable>
            </View>
            {/* Two scrollers, and both are needed: the log is long AND its widest lines are
                wider than any window. Wrapping them instead would fold a 200-character crop-id
                list into a paragraph and lose the column alignment the IoU table is read by. */}
            <ScrollView style={styles.outputScroll}>
              <ScrollView horizontal>
                <Text testID={`ml-training-output-text-${job.jobId}`} style={styles.output}>
                  {job.outputTail}
                </Text>
              </ScrollView>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      // RN-web drops accessibilityState, so selection has to reach the DOM as aria-*.
      aria-pressed={selected}
      style={[styles.chip, selected && styles.chipSelected]}
      testID={testID}
      onPress={onPress}
    >
      <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{label}</Text>
    </Pressable>
  );
}

function LinkTag({ label, url, testID }: { label: string; url: string; testID: string }) {
  return (
    <Pressable
      accessibilityRole="link"
      style={styles.linkTag}
      testID={testID}
      onPress={() => {
        void Linking.openURL(url);
      }}
    >
      <Text style={styles.linkTagText}>{label}</Text>
    </Pressable>
  );
}

function SmallButton({ onPress, label, testID }: { onPress: () => void; label: string; testID: string }) {
  return (
    <Pressable accessibilityRole="button" style={styles.refreshButton} testID={testID} onPress={onPress}>
      <Text style={styles.refreshButtonText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  heading: { color: appColors.text.primary, fontWeight: '700', fontSize: 16, marginTop: 18, marginBottom: 8 },
  refreshRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  refreshButtonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    padding: 12,
    marginBottom: 10,
    gap: 6,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardTitle: { color: appColors.text.primary, fontWeight: '700', fontSize: 14 },
  meta: { color: appColors.text.secondary, fontSize: 12 },
  hint: { color: appColors.text.muted, fontSize: 11, lineHeight: 15 },
  fieldLabel: { color: appColors.text.primary, fontWeight: '600', fontSize: 12, marginTop: 6 },
  chipRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.app,
  },
  chipSelected: { borderColor: appColors.accent.primary, backgroundColor: appColors.background.panel },
  chipText: { color: appColors.text.secondary, fontSize: 12 },
  chipTextSelected: { color: appColors.accent.primary, fontWeight: '700' },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    color: appColors.text.primary,
    backgroundColor: appColors.background.app,
    fontSize: 12,
  },
  command: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontFamily: 'monospace',
    backgroundColor: appColors.background.app,
    borderRadius: 6,
    padding: 8,
  },
  output: {
    color: appColors.text.muted,
    fontSize: 10,
    fontFamily: 'monospace',
    backgroundColor: appColors.background.app,
    borderRadius: 6,
    padding: 8,
  },
  outputButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
  },
  outputButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  outputOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  outputCard: {
    width: '100%',
    maxWidth: 900,
    // A share of the window rather than a fixed height: the point of the window is that the
    // log has room, and on a laptop that is most of the screen.
    maxHeight: '85%',
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    padding: 12,
  },
  outputHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  outputTitle: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
    flexShrink: 1,
  },
  outputClose: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '600',
  },
  outputScroll: {
    borderRadius: 6,
    backgroundColor: appColors.background.app,
  },
  runButton: {
    marginTop: 8,
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: appColors.accent.primary,
    alignItems: 'center',
  },
  runButtonDisabled: { opacity: 0.5 },
  runButtonText: { color: appColors.background.app, fontWeight: '700', fontSize: 13 },
  warning: { color: '#D7B65C', fontSize: 12 },
  ok: { color: '#3FA65A', fontSize: 12 },
  errorText: { color: '#E08A8A', fontSize: 12 },
  tagRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  linkTag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  linkTagText: { color: appColors.accent.primary, fontSize: 11, fontWeight: '600' },
  mutedTag: { color: appColors.text.muted, fontSize: 11, paddingVertical: 3 },
  badgeEligible: { color: '#3FA65A', fontSize: 11, fontWeight: '700' },
  badgeShort: { color: '#D7B65C', fontSize: 11, fontWeight: '700' },
  badgeStatus: { color: appColors.text.secondary, fontSize: 11, fontWeight: '700' },
});
